import React, { useMemo } from 'react';

import { AISmartButtonLegacy, AISmartButtonRefresh } from '@atlassian/ai-smart-button';
import { useSurfaceAIActions } from '@atlassian/ai-smart-button-api';
import { isFedRamp } from '@atlassian/atl-context';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { useSessionData } from '@confluence/session-data';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { useCreateRovoExperienceSidebarController } from '@confluence/conversation-assistant';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';

import { useCreateObjectSidebarProp } from '../hooks/useCreateObjectSidebarProp';

type AISmartButtonProps = {
	contentId: string;
	contentType: string;
	pageMode: string;
	isDisabled?: boolean;
};

export function AISmartButtonWrapper({
	contentId,
	contentType,
	pageMode,
	isDisabled = false,
}: AISmartButtonProps) {
	const { cloudId, orgId } = useSessionData();
	const isNav4Enabled = useIsNav4Enabled();
	const { setSurfaceMetadata } = useSurfaceAIActions();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const sidebarApi = useCreateRovoExperienceSidebarController();
	const currentPageMode = useGetPageMode();

	const isEnabled = !isFedRamp() && isAIEnabledForContent && isRovoEnabledForContent;
	const objectSidebar = useCreateObjectSidebarProp();

	const isEditMode = currentPageMode === PageMode.EDIT;
	// 48px = nav header height, 8px = object sidebar bottom padding, 16px = AI smart button bottom margin, 8px = extra margin so it doesn't align with nav header
	const navbarOffset = 48 + 8 + 16 + 8;
	// From confluence/next/packages/content-types-header/src/ContentTypesHeader.tsx
	const OBJECT_HEADER_CONTAINER_HEIGHT = 56;
	// From platform/packages/editor/editor-shared-styles/src/consts/consts.ts
	const FULL_PAGE_EDITOR_TOOLBAR_HEIGHT = 40;

	const totalOffset = isEditMode
		? FULL_PAGE_EDITOR_TOOLBAR_HEIGHT + OBJECT_HEADER_CONTAINER_HEIGHT + navbarOffset
		: navbarOffset;

	// only needed for legacy button
	React.useEffect(() => {
		setSurfaceMetadata({
			siteId: cloudId,
			product: 'confluence',
			contentType,
			pageMode,
		});
	}, [cloudId, setSurfaceMetadata, contentType, pageMode]);

	// Memoize analytics attributes to prevent unnecessary re-renders
	const analyticsAttributes = useMemo(
		() => ({
			pageMode,
		}),
		[pageMode],
	);

	if (!isEnabled) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.AI_MATE}>
			{fg('aifc_create_enabled') ||
			expValEquals('rovo_chat_refresh_v1_experiment', 'isRenderRefreshEnabled', true) ? (
				<AISmartButtonRefresh
					product="confluence"
					cloudId={cloudId}
					orgId={orgId}
					isNav4Enabled={isNav4Enabled}
					sidebarApi={sidebarApi}
					productPage={contentType}
					analyticsAttributes={analyticsAttributes}
					isDisabled={isDisabled}
					topOffset={totalOffset}
				/>
			) : (
				<AISmartButtonLegacy sidebarApi={objectSidebar} isDisabled={isDisabled} />
			)}
		</ErrorBoundary>
	);
}
